/**
 * SHOOTER_SET_STACK (craps) — shooter drops bills onto the stack zone.
 * Other players watch with closed wallets and will fade in the next state.
 *
 * Mirror of cee-lo's BANKER_SET_POT. Same camera / wallet / instruction
 * shape; copy is craps-flavored.
 *
 * In the collapsed craps flow, this state is also where every round
 * (after the first) lands — terminal rolls transition POINT_ROLL /
 * COME_OUT directly to SHOOTER_SET_STACK with rotated shooter,
 * skipping the legacy SHOOTER_WINS / SHOOTER_LOSES / ROUND_END /
 * STARTING beats. The previous round's profile-card dice icons,
 * HI_LO single-die outcomes, and dedup state all need to clear here
 * so the new round starts clean.
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';

export class ShooterSetStackState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    // Both `currentPlayerId` and `bankerId` are wired to the shooter
    // server-side in CrapsStrategy.getGameUpdateExtras. Either works;
    // bankerId is more stable across phase enters.
    const shooterId = update.currentPlayerId ?? update.bankerId;
    const localId = game.getLocalPlayerId();
    const isLocalShooter = !!localId && shooterId === localId;

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('betting');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();

    await game.enterBettingMode({
      open: isLocalShooter,
      animated: update.animated,
      previousState: update.previousState,
    });

    game.instructionBanner?.show(
      {
        title: "Shooter's Stack",
        text: isLocalShooter
          ? 'You are the shooter. Set your stack'
          : 'Wait for the shooter to set the stack',
      },
      update.animated,
    );

    // Round boundary — clear the previous round's dice-result icons
    // (point-hit / seven-out / natural / HI_LO single-die outcomes)
    // BEFORE _checkPlayers runs. _checkPlayers intentionally skips null
    // player.result entries (so live caches survive cross-state ticks),
    // so the round-boundary clear has to be explicit here, not derived.
    // Same call covers liveDiceResults + processedRollResults + the
    // visible profile-card icons.
    game.clearRoundResults(update.animated);
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    // Shooter doesn't shift mid-state — the entry setup stays valid.
    // Just refresh player data + dice-result icons (covers late joins
    // and balance changes).
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(_update: GameUpdate): Promise<void> {
    // Clear the BETTING activity pill — server's isBetting reset for
    // each player may not have landed yet. FADING reuses the betting
    // camera + wallet on its own entry (idempotent), no teardown there.
    this._clearBettingActivity();
  }
}
